import {setDisplayName} from 'recompose'

import type * as React from 'react'
import {memo} from 'react'

import {getDisplayName, wrapDisplayName} from '../library/utils/getDisplayName'
import type {Enhancer} from '../types'

const isStatelessFunctionalComponent = (
  Component: React.ElementType,
): Component is React.FunctionComponent =>
  typeof Component === 'function' && !Component.prototype?.isReactComponent

const createFactoryIfNeeded: <P>(
  Component: React.ElementType,
) => React.FunctionComponent<P> = Component =>
  isStatelessFunctionalComponent(Component) // eslint-disable-next-line new-cap
    ? props => Component({...Component.defaultProps, ...props})
    : props => <Component {...props} />

export default function withHook<AddProps, OwnProps extends {}>(
  useHook: (arg0: OwnProps) => AddProps,
  wrapperName = 'withHook',
): Enhancer<AddProps, OwnProps> {
  return BaseComponent => {
    const factory = createFactoryIfNeeded(BaseComponent)

    const WithHook = memo(function WithHookInner(props: OwnProps) {
      const addProps = useHook(props)
      return factory({...props, ...addProps})
    })

    if (process.env.NODE_ENV !== 'production') {
      return setDisplayName(
        isStatelessFunctionalComponent(BaseComponent)
          ? getDisplayName(BaseComponent)
          : wrapDisplayName(BaseComponent, wrapperName),
      )(WithHook)
    }

    return WithHook
  }
}
